import { DETAIL_PIECES, type DetailPiece } from "@eldrun/content";
import { BoxGeometry, CircleGeometry, ConeGeometry, CylinderGeometry, DodecahedronGeometry, IcosahedronGeometry, type BufferGeometry } from "three";
import { DETAIL_MODELS, MODELS, type ModelId } from "../assets/models.generated.ts";
import { lumpy, merge, type Piece } from "./mesh-kit.ts";
import type { ModelLibrary } from "./model-library.ts";
import { pieceGeometry } from "./poi-meshes.ts";

const ROCK = "#8A8578";
const SLAB = "#9A958B";
const BARK = "#6B4A30";
const CUT = "#B89060";
const NEEDLE = "#2F6B3A";
const LEAF = "#4F8F3E";
const DRIFTWOOD = "#A08A6C";

/** Peças lidas pelo id de outro pacote: a pedra solta é a mesma das montagens dos POIs. */
const SHARED: Partial<Record<DetailPiece, ModelId>> = { "pedra-solta": "pedra-solta" };

/**
 * Ajusta a geometria às medidas da peça (largura, altura, comprimento), com a base em y = 0 e o centro
 * da caixa em xz = 0. Só para as procedurais simétricas; o tronco tem o eixo em x = 0 (o toco sai para +x).
 */
function fit(g: BufferGeometry, [w, h, d]: readonly [number, number, number]): BufferGeometry {
  g.computeBoundingBox();
  const { min, max } = g.boundingBox!;
  g.translate(-(min.x + max.x) / 2, -min.y, -(min.z + max.z) / 2);
  g.scale(w / (max.x - min.x), h / (max.y - min.y), d / (max.z - min.z));
  g.computeBoundingSphere();
  return g;
}

/** Tronco caído ao longo de z (raio 0,18 m, 2,6 m), o corte claro nas pontas e um toco de galho para +x, baixo. */
function log(): BufferGeometry {
  const length = DETAIL_PIECES["tronco-caido"].size[2];
  const r = 0.18;
  const caps = [-1, 1].map((s): Piece => ({ geometry: new CircleGeometry(r * 0.94, 6), color: CUT, position: [0, r, (s * length) / 2], rotation: [0, s > 0 ? 0 : Math.PI, 0] }));
  return merge([
    { geometry: new CylinderGeometry(r * 0.94, r, length, 6, 1, true), color: BARK, position: [0, r, 0], rotation: [Math.PI / 2, 0, 0] },
    ...caps,
    // Toco quase deitado, saindo da casca a 20° acima do meio: não passa da altura do tronco.
    { geometry: new ConeGeometry(0.05, 0.34, 4, 1, true), color: BARK, position: [0.32, 0.29, 0.45], rotation: [0, 0, -1.3] },
  ]);
}

/**
 * Reservas procedurais dos enfeites (ADR 0007): nas medidas de `DETAIL_PIECES`, base em y = 0. As
 * mudas balançam pela altura; o resto é rígido.
 */
const PROCEDURAL: Record<DetailPiece, () => BufferGeometry> = {
  pedrinha: () => fit(merge([{ geometry: lumpy(new DodecahedronGeometry(0.2, 0), 0.3, 21), color: ROCK }]), DETAIL_PIECES.pedrinha.size),
  "pedra-solta": () => pieceGeometry("pedra-solta"),
  "laje-pedra": () => fit(merge([{ geometry: lumpy(new DodecahedronGeometry(0.6, 0), 0.2, 23), color: SLAB }]), DETAIL_PIECES["laje-pedra"].size),
  "muda-conifera": () =>
    fit(
      merge([
        { geometry: new CylinderGeometry(0.025, 0.035, 0.4, 4), color: BARK, wind: [0, 0.15], position: [0, 0.2, 0] },
        ...[0, 1, 2].map((i): Piece => ({ geometry: new ConeGeometry(0.27 - i * 0.07, 0.5 - i * 0.08, 6), color: NEEDLE, wind: [0.15 + i * 0.12, 0.3 + i * 0.12], position: [0, 0.5 + i * 0.25, 0] })),
      ]),
      DETAIL_PIECES["muda-conifera"].size,
    ),
  "muda-folhosa": () =>
    fit(
      merge([
        { geometry: new CylinderGeometry(0.025, 0.04, 0.6, 4), color: BARK, wind: [0, 0.2], position: [0, 0.3, 0] },
        { geometry: new IcosahedronGeometry(0.32, 0), color: LEAF, wind: [0.3, 0.5], position: [0, 0.85, 0], scale: [1.25, 0.9, 1] },
        { geometry: new IcosahedronGeometry(0.2, 0), color: "#5FA04A", wind: [0.25, 0.4], position: [0.22, 0.7, 0.05] },
      ]),
      DETAIL_PIECES["muda-folhosa"].size,
    ),
  "tronco-caido": log,
  tabua: () => fit(merge([{ geometry: new BoxGeometry(1, 1, 1), color: DRIFTWOOD }]), DETAIL_PIECES.tabua.size),
};

const cache = new Map<DetailPiece, BufferGeometry>();

/** Malha procedural da peça (cacheada): a única do tronco caído e a reserva das outras. */
export function detailFallback(piece: DetailPiece): BufferGeometry {
  let g = cache.get(piece);
  if (!g) {
    g = PROCEDURAL[piece]();
    cache.set(piece, g);
  }
  return g;
}

export interface DetailMesh {
  geometry: BufferGeometry;
  /** Paletas da malha, na ordem dos atributos de cor (as mudas: normal, dry e cold); vazio nas procedurais. */
  palettes: readonly string[];
}

/** Modelo do pipeline (do pacote `detalhes`, ou de outro pelo id) ou a reserva procedural. */
export function detailMesh(piece: DetailPiece, library: ModelLibrary): DetailMesh {
  const model = DETAIL_MODELS[piece] ?? SHARED[piece];
  const geometry = model ? library.get(model, 0) : undefined;
  return geometry && model ? { geometry, palettes: MODELS[model].palettes } : { geometry: detailFallback(piece), palettes: [] };
}
